import React from 'react';
import clsx from 'clsx';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'xs' | 'sm' | 'md' | 'lg';
  loading?: boolean;
  disabled?: boolean;
  icon?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

/**
 * Button Component
 * 
 * A refined button component following the editorial serif aesthetic.
 * Supports variants: primary (burnished gold), secondary (outline), ghost, and danger.
 */
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      disabled = false,
      icon,
      children,
      className,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading;

    const baseClasses = clsx(
      'inline-flex items-center justify-center gap-2',
      'font-sans font-medium whitespace-nowrap',
      'transition-all duration-200 ease-out',
      'touch-manipulation',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2',
      'disabled:opacity-50 disabled:pointer-events-none disabled:cursor-not-allowed'
    );

    const variantClasses = {
      primary: clsx(
        'bg-accent text-accent-foreground',
        'rounded-md shadow-sm',
        'hover:bg-accent-secondary hover:shadow-accent hover:-translate-y-0.5',
        'active:translate-y-0'
      ),
      secondary: clsx(
        'bg-transparent text-foreground',
        'border border-foreground rounded-md',
        'hover:bg-muted hover:border-accent hover:text-accent'
      ),
      ghost: clsx(
        'bg-transparent text-muted-foreground',
        'hover:text-foreground hover:underline hover:decoration-accent hover:underline-offset-4'
      ),
      danger: clsx(
        'bg-error text-white',
        'rounded-md shadow-sm',
        'hover:bg-error/90 hover:-translate-y-0.5',
        'active:translate-y-0'
      ),
    };

    const sizeClasses = {
      xs: clsx('px-2.5 py-1.5 text-xs min-h-[32px]'),
      sm: clsx('px-4 py-2 text-sm min-h-[40px]'),
      md: clsx('px-6 py-3 text-sm min-h-[44px]'),
      lg: clsx('px-8 py-4 text-base min-h-[48px]'),
    };

    return (
      <button
        ref={ref}
        disabled={isDisabled}
        className={clsx(
          baseClasses,
          variantClasses[variant],
          sizeClasses[size],
          className
        )}
        {...props}
      >
        {loading ? (
          <svg
            className="animate-spin h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        ) : (
          icon && <span className="flex-shrink-0">{icon}</span>
        )}
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';
